// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Admin panel  (the shell for every /admin/* page)
// Same site, same URL space: /admin is the back office for signed-in STAFF. This shell is the guard and the frame — nobody without a staff
// role claim gets past it (customers, guests and signed-out visitors are sent to /welcome/staff), and it renders the top bar, the navigation
// and the signed-in member's role. Pages render into <Outlet />. Site chrome (navbar, footer, chat widgets) is hidden on /admin (App.tsx).
// Real enforcement is the backend's: every /api/admin call re-checks the role; the UI only hides what a role can't do.
// Navigation is a categorized mega-menu: ~30 screens grouped into 6 categories (Operations, Customers & Support, Catalog & Marketing,
// Finance & Delivery, Governance & Settings, Insights) plus a pinned Overview link — see CATEGORIES below. Each category is a disclosure
// button: hovering opens it on a mouse (the button and its panel share one hoverable wrapper so crossing the seam between them never
// flickers), a click/Enter/Space opens or closes it from a keyboard or on touch, Escape closes it and returns focus to the button, and a
// click outside or a route change closes whatever's open. A panel lists that category's screens as icon + one-line "what it's for" cards
// (CategoryMenu below) — only one category is ever open, so nothing overlaps. Each panel is positioned in JS (fixed, clamped to the
// viewport) so a category near the edge — the last one, "Insights" — always opens fully on-screen, never needing a horizontal scrollbar.
// TO ADD A SCREEN: create pages/admin/<Name>.tsx, add a <Route> under /admin in App.tsx and an entry to the right group in CATEGORIES below.
// Neighbours: components/AuthProvider (role), utils/adminApi, pages/StaffLogin.
// ─────────────────────────────────────────────────────────────────────────────
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet, useLocation } from "react-router-dom";
import { Ban, Settings2, Anchor, BarChart3, Boxes, Building2, ChevronDown, Megaphone, PackageOpen, TicketPercent, CreditCard, Scale, Landmark, MapPinned, CalendarClock, Clock, BookOpen, ClipboardList, Fingerprint, Fish, Star, IdCard, LayoutDashboard, Loader2, LogOut, LifeBuoy, MessageSquareQuote, MessageSquareWarning, ShieldCheck, Thermometer, Trash2, Truck, UserCog, Users, type LucideIcon } from "lucide-react";
import useAuth from "../../hooks/useAuth";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { displayName } from "../../utils/staffAuth";

const OVERVIEW = { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true };

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /** The small "what this screen is about" line shown under the label in the mega-menu card. */
  desc: string;
  /** Opens a "Coming soon" preview (pages/admin/AdminSoon); everything else below is live. */
  soon?: boolean;
  /** Hidden from editors/viewers — the page itself refuses them too (the backend is what really enforces it). */
  adminOnly?: boolean;
}
interface NavCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  items: NavItem[];
}

const CATEGORIES: NavCategory[] = [
  {
    id: "operations", label: "Operations", icon: Truck,
    items: [
      { to: "/admin/orders", label: "Order management", icon: ClipboardList, desc: "Confirm, prepare and dispatch every order" },
      { to: "/admin/bulk-orders", label: "B2B & bulk orders", icon: Building2, desc: "Restaurant & party-feast orders, logged by staff" },
      { to: "/admin/dispatch", label: "Fleet dispatch", icon: Truck, desc: "Hand confirmed orders to an available captain" },
      { to: "/admin/drivers", label: "Fleet & drivers", icon: IdCard, desc: "Captains, verification and duty status" },
      { to: "/admin/landings", label: "Harbor landings", icon: Anchor, desc: "Dockside catch intake — the Daily Catch backend" },
      { to: "/admin/cancel-reasons", label: "Cancellation reasons", icon: Ban, desc: "Reasons customers, drivers and staff pick" },
    ],
  },
  {
    id: "customers", label: "Customers & Support", icon: Users,
    items: [
      { to: "/admin/customers", label: "Customers", icon: Users, desc: "Directory of every customer account" },
      { to: "/admin/claims", label: "Complaints & refunds", icon: MessageSquareWarning, desc: "Problem claims on delivered orders" },
      { to: "/admin/tickets", label: "Support tickets", icon: LifeBuoy, desc: "The grievance log staff keep by hand" },
      { to: "/admin/reviews", label: "Ratings & reviews", icon: Star, desc: "Customer reviews and moderation" },
      { to: "/admin/testimonials", label: "Testimonials", icon: MessageSquareQuote, desc: "Shortlist verbatim quotes from reviews" },
      { to: "/admin/requests", label: "Data requests", icon: Trash2, desc: "Erasure requests waiting on staff" },
    ],
  },
  {
    id: "catalog", label: "Catalog & Marketing", icon: Megaphone,
    items: [
      { to: "/admin/products", label: "Products", icon: Fish, desc: "Read-only seafood catalog" },
      { to: "/admin/banners", label: "Home banners", icon: Megaphone, desc: "Home & Menu banner manager" },
      { to: "/admin/promotions", label: "Promotions & coupons", icon: TicketPercent, desc: "Coupon codes customers redeem at checkout" },
      { to: "/admin/faq", label: "FAQ manager", icon: BookOpen, desc: "Knowledge-base articles and drafting" },
    ],
  },
  {
    id: "finance", label: "Finance & Delivery", icon: Landmark,
    items: [
      { to: "/admin/payments", label: "Payments & APIs", icon: CreditCard, desc: "Razorpay status, wallet float, pause switch" },
      { to: "/admin/finance", label: "Finance & tax", icon: Landmark, desc: "GSTIN, GST slabs, delivery fee rules" },
      { to: "/admin/zones", label: "Delivery zones", icon: MapPinned, desc: "Draw zones — enforced at checkout" },
      { to: "/admin/service-hours", label: "Service hours", icon: Clock, desc: "Weekly ordering & dispatch timetable" },
      { to: "/admin/slots", label: "Delivery slots", icon: CalendarClock, desc: "Delivery windows and capacity" },
    ],
  },
  {
    id: "governance", label: "Governance & Settings", icon: Scale,
    items: [
      { to: "/admin/policies", label: "Policy & legal", icon: Scale, desc: "Draft, preview and publish Terms & Privacy" },
      { to: "/admin/app-settings", label: "App settings", icon: Settings2, desc: "Contact lines, socials, storefront pause" },
      { to: "/admin/security", label: "Security", icon: Fingerprint, desc: "Staff fingerprint sign-in" },
      { to: "/admin/team", label: "Team & access", icon: UserCog, desc: "Staff accounts, roles and audit log", adminOnly: true },
    ],
  },
  {
    id: "insights", label: "Insights", icon: BarChart3,
    items: [
      { to: "/admin/soon/telemetry", label: "Cold-chain telemetry", icon: Thermometer, desc: "Coming soon — reefer & IoT sensors", soon: true },
      { to: "/admin/soon/cold-room-inventory", label: "Cold-room inventory", icon: Boxes, desc: "Coming soon — cold-room aging matrix", soon: true },
      { to: "/admin/soon/eco-box", label: "Eco-box tracking", icon: PackageOpen, desc: "Coming soon — reusable packaging ledger", soon: true },
      { to: "/admin/soon/financial-analytics", label: "Financial analytics", icon: BarChart3, desc: "Coming soon — spoilage & unit economics", soon: true },
      { to: "/admin/soon/analytics", label: "Reports & analytics", icon: BarChart3, desc: "Coming soon — broader reporting", soon: true },
    ],
  },
];

const navPill = (active: boolean) =>
  `inline-flex min-h-11 items-center gap-1.5 rounded-full px-4 text-sm font-bold focus-visible:outline-2 focus-visible:outline-teal-600 ${active ? "bg-ocean-800 text-white" : "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50"}`;

/**
 * One category's disclosure button + mega-panel. Button and panel sit in one `relative` wrapper so the hover
 * zone spans both with no gap to "fall through" between them: the panel is `position: fixed` at exactly
 * `rect.bottom` (flush against the button, zero gap — the visual gap is inner padding, not an outer margin) and
 * its `left`/`width` are computed in JS and clamped to the viewport, so a category near either edge (e.g. the
 * last one, "Insights") never opens a panel that runs off-screen or needs a horizontal scrollbar to reach.
 */
function CategoryMenu({ category, role, isOpen, onOpen, onClose, pathname }: { category: NavCategory; role: string; isOpen: boolean; onOpen: () => void; onClose: () => void; pathname: string }) {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const items = category.items.filter((item) => !item.adminOnly || role === "admin");

  // Re-measure whenever the panel opens (and on resize while it's open) — before paint, so there's no flash
  // of a mis-positioned panel. Hooks must run unconditionally, so this sits above the `items.length === 0`
  // guard below (a no-op then, since `isOpen` can never be true for a category nothing renders a button for).
  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;
    const compute = () => {
      const rect = buttonRef.current!.getBoundingClientRect();
      const margin = 16;
      const width = Math.min(window.innerWidth - margin * 2, 560);
      const idealLeft = rect.left + rect.width / 2 - width / 2;
      const left = Math.min(Math.max(idealLeft, margin), Math.max(margin, window.innerWidth - width - margin));
      setPos({ top: rect.bottom, left, width });
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [isOpen]);

  if (items.length === 0) return null;
  const isItemActive = (item: NavItem) => pathname === item.to || pathname.startsWith(`${item.to}/`);
  const active = items.some(isItemActive);
  const panelId = `admin-nav-panel-${category.id}`;

  return (
    <div
      className="relative"
      onMouseEnter={onOpen}
      onMouseLeave={onClose}
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !isOpen) return;
        event.stopPropagation();
        onClose();
        buttonRef.current?.focus();
      }}
    >
      <button ref={buttonRef} type="button" aria-haspopup="true" aria-expanded={isOpen} aria-controls={panelId} onClick={() => (isOpen ? onClose() : onOpen())} className={navPill(active)}>
        <category.icon className="h-4 w-4" aria-hidden="true" /> {category.label}
        <ChevronDown className={`h-3.5 w-3.5 transition-transform motion-reduce:transition-none ${isOpen ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      <div id={panelId} hidden={!isOpen} style={{ top: pos.top, left: pos.left, width: pos.width }} className="fixed z-50 pt-2">
        <div role="group" aria-label={`${category.label} screens`} className="grid grid-cols-1 gap-1 rounded-2xl bg-white p-3 shadow-xl ring-1 ring-ocean-900/10 sm:grid-cols-2 dark:bg-ocean-900 dark:ring-white/15">
          {items.map((item) => (
            <Link key={item.to} to={item.to} onClick={onClose} className={`flex items-start gap-3 rounded-xl p-2.5 text-left focus-visible:outline-2 focus-visible:outline-teal-600 ${isItemActive(item) ? "bg-ocean-700/10 dark:bg-white/10" : "hover:bg-ocean-700/5 dark:hover:bg-white/5"}`}>
              <span aria-hidden="true" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-teal-500/15 text-teal-700 dark:text-teal-300">
                <item.icon className="h-4 w-4" />
              </span>
              <span>
                <span className="flex items-center gap-1.5 text-sm font-bold">
                  {item.label}
                  {item.soon && <span className="rounded-full bg-coral-500/20 px-1.5 text-[10px] uppercase text-coral-600 dark:text-coral-300">Soon</span>}
                </span>
                <span className="block text-xs text-ocean-800/80 dark:text-foam-100/75">{item.desc}</span>
              </span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminShell() {
  useDocumentTitle("Admin");
  const auth = useAuth();
  const { pathname } = useLocation();
  const [signingOut, setSigningOut] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const navRef = useRef<HTMLDivElement>(null);

  // Close whatever's open on navigation, and on a click/tap outside the nav (the effect only listens while
  // something is actually open, so it costs nothing on every other render).
  useEffect(() => { setOpenId(null); }, [pathname]);
  useEffect(() => {
    if (!openId) return;
    const onPointerDown = (event: PointerEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) setOpenId(null);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [openId]);

  if (auth.status === "loading") {
    return <p role="status" className="flex min-h-screen items-center justify-center gap-2 bg-foam-50 dark:bg-ocean-950"><Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> Loading…</p>;
  }
  if (auth.status !== "signed-in" || !auth.user?.role) return <Navigate to="/welcome/staff" replace />;

  const { role, email } = auth.user;
  return (
    <div className="min-h-screen bg-foam-50 text-ocean-950 dark:bg-ocean-950 dark:text-foam-50">
      <header className="sticky top-0 z-40 border-b print:hidden border-ocean-900/10 bg-white/95 backdrop-blur-md dark:border-white/10 dark:bg-ocean-900/95">
        <div className="mx-auto flex max-w-[1500px] items-center gap-3 px-4 py-3">
          <Link to="/admin" className="flex items-center gap-2 font-display text-lg font-bold"><ShieldCheck className="h-5 w-5 text-teal-700" aria-hidden="true" /> ZenFresh</Link>
          <span className="rounded-full bg-ocean-800 px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider text-white">Command Ops</span>
          <span className="ml-auto text-right text-xs">
            <span className="block font-bold">{displayName(email)}</span>
            <span className="rounded-full bg-teal-100 px-2 py-0.5 font-bold capitalize text-teal-700" data-testid="role">{role}</span>
          </span>
          <button
            type="button"
            disabled={signingOut}
            onClick={() => {
              setSigningOut(true);
              void auth.signOut().finally(() => setSigningOut(false));
            }}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-full bg-ocean-700/10 px-4 text-sm font-bold disabled:opacity-60 dark:bg-white/10"
          >
            <LogOut className="h-4 w-4" aria-hidden="true" /> Sign out
          </button>
        </div>
        <nav aria-label="Admin sections" ref={navRef} className="mx-auto max-w-[1500px] px-4 pb-2">
          <ul className="flex flex-wrap gap-2">
            <li>
              <NavLink to={OVERVIEW.to} end={OVERVIEW.end} className={({ isActive }) => navPill(isActive)}>
                <OVERVIEW.icon className="h-4 w-4" aria-hidden="true" /> {OVERVIEW.label}
              </NavLink>
            </li>
            {CATEGORIES.map((category) => (
              <li key={category.id}>
                <CategoryMenu category={category} role={role} pathname={pathname} isOpen={openId === category.id} onOpen={() => setOpenId(category.id)} onClose={() => setOpenId((current) => (current === category.id ? null : current))} />
              </li>
            ))}
          </ul>
        </nav>
      </header>
      <main className="mx-auto max-w-[1500px] px-4 py-6">
        {role === "viewer" && <p className="mb-4 rounded-xl bg-ocean-700/10 p-3 text-sm font-semibold dark:bg-white/10">You have read-only access. Changing orders, complaints and requests needs an editor or admin.</p>}
        <Outlet />
      </main>
    </div>
  );
}
